import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import CaregiverProfileEditor from '../components/CaregiverProfileEditor';
import { getCaregiverById, updateUser as updateUserFirestore } from '../services/firestoreService';

const EditCaregiverProfile = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    try {
      const userId = user?.uid || user?.id;
      const result = await getCaregiverById(userId);
      
      if (result.success && result.caregiver) {
        const userData = result.caregiver;
        setProfile({
          userId: userId,
          title: userData.title || 'Professional Pet Caregiver',
          bio: userData.bio || '',
          hourlyRate: userData.hourlyRate || 250,
          maxPets: userData.maxPets || 3,
          speciesAccepted: userData.speciesAccepted || ['Dog', 'Cat'],
          location: userData.location || '',
          badges: userData.badges || ['verified'],
          isAvailable: userData.isAvailable !== false,
          isBackgroundChecked: userData.isBackgroundChecked || false,
          homePhotos: userData.homePhotos || []
        });
        setShowEditor(true);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    try {
      const userId = user?.uid || user?.id;
      
      // Update in both AuthContext and Firestore
      await updateUser({
        title: formData.title,
        bio: formData.bio,
        hourlyRate: formData.hourlyRate,
        maxPets: formData.maxPets,
        speciesAccepted: formData.speciesAccepted,
        location: formData.location,
        badges: formData.badges,
        isAvailable: formData.isAvailable,
        isBackgroundChecked: formData.isBackgroundChecked,
        homePhotos: formData.homePhotos
      });

      // Also update directly in Firestore to ensure consistency
      await updateUserFirestore(userId, {
        title: formData.title,
        bio: formData.bio,
        hourlyRate: formData.hourlyRate,
        maxPets: formData.maxPets,
        speciesAccepted: formData.speciesAccepted,
        location: formData.location,
        badges: formData.badges,
        isAvailable: formData.isAvailable,
        isBackgroundChecked: formData.isBackgroundChecked,
        homePhotos: formData.homePhotos
      });

      // Redirect back to dashboard
      navigate('/caregiver-dashboard');
    } catch (error) {
      console.error('Error saving profile:', error);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="pf-page-wrap flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--pf-accent)] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link 
          to="/caregiver-dashboard" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Professional Profile</h1>
          <p className="text-gray-600 mb-6">
            Update your professional details to attract more pet owners
          </p>

          <button
            onClick={() => setShowEditor(true)}
            className="pf-btn-primary px-6 py-3 rounded-xl font-semibold"
          >
            Edit Professional Profile
          </button>
        </div>

        {showEditor && profile && (
          <CaregiverProfileEditor
            isOpen={showEditor}
            onClose={() => navigate('/caregiver-dashboard')}
            profile={profile}
            onSave={handleSave}
          />
        )}
      </div>
    </div>
  );
};

export default EditCaregiverProfile;

